import { useEffect } from "react";

/**
 * Warns the user before they leave (tab close / reload / browser back) while there are unsaved
 * canvas changes. Shared by the RDV and Data Mart modelling workspaces — both persist layout only
 * on an explicit "Save Layout" click, so without this a navigation silently discards hand-arranged
 * positions (the #1 "I lost my work" complaint).
 *
 * Browsers ignore custom text for the native beforeunload prompt; setting returnValue is what
 * triggers the confirmation dialog. In-app (react-router) navigation blocking is intentionally NOT
 * handled here to avoid coupling to a router version — callers that need it can add a route guard.
 */
export function useUnsavedChangesGuard(hasUnsavedChanges: boolean): void {
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required for Chrome to show the prompt.
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);
}
